/**
 * Landing screen for the password-reset deep link
 * (/app/?reset_key=…&reset_login=…). store.js's bootstrap() consumes
 * those params — regardless of login state, the click expresses intent
 * and may target a different account than the one logged in — strips
 * them from the URL and routes here with {key, login}. On success the
 * backend response has the exact login() shape, so onAuthenticated()
 * logs the user straight in: they just proved control of the account's
 * mailbox, the same proof register() accepts.
 *
 * Auth-family screen: no bottom nav, no desktop two-pane shell (see
 * app.jsx — the Aankomend panel would fire an unauthorized /tasks fetch
 * next to this form).
 */
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api.js';
import { onAuthenticated, resetTo, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import RecaptchaNotice from '../components/RecaptchaNotice.jsx';
import {
  preloadRecaptcha,
  getRecaptchaToken,
  recaptchaScriptFailed,
} from '../lib/recaptcha.js';

export default function ResetPasswordScreen({ params }) {
  const key = params?.key || '';
  const login = params?.login || '';

  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // A link without both params, or one the server rejected, can only be
  // fixed by requesting a fresh link — same recovery either way.
  const [linkInvalid, setLinkInvalid] = useState(!key || !login);

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  // Same decoration as AuthScreen's authError(): when the reCAPTCHA
  // script itself never loaded (adblocker), name the real problem.
  function resetError(err) {
    if (err && err.code === 'etm_recaptcha_failed' && recaptchaScriptFailed()) {
      return __('De beveiligingscontrole (reCAPTCHA) kon niet geladen worden. Schakel je adblocker uit voor deze site en probeer het opnieuw.', 'etm');
    }
    return friendlyError(err);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError('');

    // Cheap local checks; neither sends a request.
    if (password.length < 8) {
      setError(__('Wachtwoord moet minstens 8 tekens zijn.', 'etm'));
      return;
    }
    if (password !== passwordRepeat) {
      setError(__('Wachtwoorden komen niet overeen.', 'etm'));
      return;
    }

    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('reset');
      const res = await api.auth.resetPassword({
        login,
        key,
        password,
        recaptcha_token: recaptchaToken,
      });
      await onAuthenticated(res);
      showToast(__('Wachtwoord aangepast.', 'etm'), 'success');
    } catch (err) {
      const message = resetError(err);
      setError(message);
      if (err && err.code === 'etm_reset_key_invalid') {
        setLinkInvalid(true);
      } else {
        showToast(message, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="etm-login-container">
      <div class="etm-login-logo">ETM</div>
      <p class="etm-login-subtitle">{__('Nieuw wachtwoord instellen', 'etm')}</p>

      {linkInvalid ? (
        <div class="etm-reset-invalid">
          <p class="etm-form-error">
            {error || __('Deze herstellink is ongeldig of verlopen.', 'etm')}
          </p>
          <button
            type="button"
            class="etm-btn etm-btn-primary"
            onClick={() => resetTo('auth', { mode: 'forgot' })}
          >
            {__('Vraag een nieuwe link aan', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-secondary"
            onClick={() => resetTo('auth')}
          >
            {__('Terug naar inloggen', 'etm')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-reset-password">
              {__('Nieuw wachtwoord', 'etm')}
            </label>
            <input
              id="etm-reset-password"
              class="etm-form-input"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
          </div>
          <div class="etm-input-group">
            <label class="etm-form-label" for="etm-reset-password-repeat">
              {__('Herhaal wachtwoord', 'etm')}
            </label>
            <input
              id="etm-reset-password-repeat"
              class="etm-form-input"
              type="password"
              autoComplete="new-password"
              required
              value={passwordRepeat}
              onInput={(e) => setPasswordRepeat(e.currentTarget.value)}
            />
          </div>
          <p class="etm-field-hint">{__('Minimaal 8 tekens.', 'etm')}</p>

          {error ? <p class="etm-form-error">{error}</p> : null}

          <button type="submit" class="etm-btn etm-btn-primary" disabled={submitting}>
            {submitting ? __('Bezig…', 'etm') : __('Wachtwoord opslaan', 'etm')}
          </button>
          <button
            type="button"
            class="etm-btn etm-btn-ghost"
            disabled={submitting}
            onClick={() => resetTo('auth')}
          >
            {__('Terug naar inloggen', 'etm')}
          </button>

          <RecaptchaNotice />
        </form>
      )}
    </div>
  );
}
